'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { ExternalLink } from 'lucide-react'
import { cn } from '@/lib/utils'

export const APP_STORE_URL =
  'https://apps.apple.com/us/app/wagamour/id6772781719'
export const GOOGLE_PLAY_URL =
  'https://play.google.com/store/apps/details?id=com.wagamour.app'

const APP_STORE_BADGE_URL =
  'https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg'
const GOOGLE_PLAY_BADGE_URL =
  'https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png'
/** Hero-only assets with transparent canvas padding already normalized. */
const HERO_APP_STORE_BADGE_SRC = '/marketing/store-badges/app-store-badge.svg'
const HERO_GOOGLE_PLAY_BADGE_SRC = '/marketing/store-badges/google-play-badge.png'

type StoreKind = 'apple' | 'google' | 'pwa'
type DevicePlatform = 'desktop' | 'ios' | 'android'

/** Hero-only height. Store artwork keeps its intrinsic ratio; Open Wagamour sizes to its label. */
const HERO_CTA_SURFACE =
  'box-border h-[46px] min-h-[46px] max-h-[46px]'

type StoreDownloadButtonsProps = {
  className?: string
  buttonClassName?: string
  size?: 'md' | 'lg'
  /** Hero-only chrome. Footer and /play/connect keep the default treatment. */
  appearance?: 'default' | 'hero'
  includePwa?: boolean
  deviceAware?: boolean
  /** Browser web-app launch path. Defaults to /app-beta for /play/connect. Marketing passes /app. */
  webAppHref?: string
  onStoreClick?: (store: StoreKind) => void
}

function resolvePlatform(): DevicePlatform {
  if (typeof navigator === 'undefined') return 'desktop'
  const ua = navigator.userAgent || ''
  if (/Android/i.test(ua)) return 'android'
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios'
  return 'desktop'
}

function badgeHeight(size: 'md' | 'lg') {
  return size === 'lg' ? 48 : 42
}

function StoreBadge({
  label,
  href,
  imageSrc,
  imageAlt,
  size,
  kind,
  appearance = 'default',
  className,
  onClick,
}: {
  label: string
  href: string
  imageSrc: string
  imageAlt: string
  size: 'md' | 'lg'
  kind: 'apple' | 'google'
  appearance?: 'default' | 'hero'
  className?: string
  onClick?: () => void
}) {
  const isHero = appearance === 'hero'
  const imageHeight = badgeHeight(size)
  const heroSrc =
    kind === 'google' ? HERO_GOOGLE_PLAY_BADGE_SRC : HERO_APP_STORE_BADGE_SRC

  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      onClick={onClick}
      data-marketing-hero-cta-item={isHero ? kind : undefined}
      data-hero-cta-surface={isHero ? kind : undefined}
      className={cn(
        'group inline-flex items-center justify-center transition-[transform,filter,border-color,background-color] hover:scale-[1.02] active:scale-[0.99]',
        isHero
          ? cn(
              HERO_CTA_SURFACE,
              'relative w-auto max-w-full shrink-0 items-center justify-center border-0 bg-transparent p-0 shadow-none hover:bg-transparent hover:brightness-[1.05]'
            )
          : cn(
              'w-full rounded-[22px] border border-[#FFF6E8]/18 bg-[#07060A]/86 shadow-[inset_0_1px_0_rgba(255,246,232,0.2),0_18px_48px_-28px_rgba(255,127,164,0.55)] backdrop-blur-xl hover:border-[#FFF6E8]/35 hover:bg-[#111016] hover:brightness-[1.05]',
              size === 'lg'
                ? 'min-h-[64px] px-4 py-2.5'
                : 'min-h-[56px] px-3 py-2'
            ),
        className
      )}
    >
      <img
        src={isHero ? heroSrc : imageSrc}
        alt={imageAlt}
        height={isHero ? 46 : imageHeight}
        data-hero-cta-art={isHero ? kind : undefined}
        className={
          isHero
            ? 'pointer-events-none h-[46px] w-auto max-h-[46px] select-none object-contain object-center'
            : 'block h-auto max-h-full w-auto max-w-[92%] object-contain object-center'
        }
        style={isHero ? { height: '46px', width: 'auto' } : { height: `${imageHeight}px` }}
      />
    </Link>
  )
}

function PwaBadge({
  size,
  visualPriority = 'primary',
  appearance = 'default',
  className,
  href = '/app-beta',
  onClick,
}: {
  size: 'md' | 'lg'
  visualPriority?: 'primary' | 'secondary'
  appearance?: 'default' | 'hero'
  className?: string
  href?: string
  onClick?: () => void
}) {
  const isHero = appearance === 'hero'

  return (
    <Link
      href={href}
      aria-label="Open Wagamour on the web"
      onClick={onClick}
      data-marketing-hero-cta-item={isHero ? 'pwa' : undefined}
      data-hero-cta-surface={isHero ? 'pwa' : undefined}
      className={cn(
        'group inline-flex items-center justify-center font-bold transition-[transform,filter,border-color,background-color] hover:scale-[1.02] active:scale-[0.99]',
        isHero
          ? cn(
              HERO_CTA_SURFACE,
              'w-fit shrink-0 gap-1.5 rounded-[10px] border-0 px-2.5 text-sm whitespace-nowrap',
              visualPriority === 'secondary'
                ? 'bg-[#FFF6E8]/14 text-[#FFF6E8] shadow-[0_10px_28px_-22px_rgba(255,246,232,0.4)] hover:bg-[#FFF6E8]/20 hover:text-white'
                : 'bg-[linear-gradient(90deg,#FF8A65,#FF7FA4,#F6B443)] text-white shadow-[0_12px_32px_-20px_rgba(255,127,164,0.6)] hover:brightness-[1.04]'
            )
          : cn(
              'gap-3 rounded-[22px] border',
              visualPriority === 'secondary'
                ? 'border-[#FFF6E8]/28 bg-[#FFF6E8]/10 text-[#FFF6E8] shadow-[inset_0_1px_0_rgba(255,246,232,0.18),0_14px_36px_-28px_rgba(255,246,232,0.42)] backdrop-blur-xl hover:border-[#FFF6E8]/42 hover:bg-[#FFF6E8]/16 hover:text-white'
                : 'border-[#FFF6E8]/22 bg-[linear-gradient(90deg,#FF8A65,#FF7FA4,#F6B443)] text-white shadow-[0_18px_48px_-22px_rgba(255,127,164,0.65)] hover:brightness-[1.04]',
              size === 'lg'
                ? 'min-h-[64px] px-5 py-3.5 text-base'
                : 'min-h-[56px] px-4 py-3 text-sm'
            ),
        className
      )}
    >
      <span>Open Wagamour</span>
      <ExternalLink
        className={isHero ? 'h-3.5 w-3.5' : 'h-4 w-4'}
        aria-hidden
      />
    </Link>
  )
}

export function StoreDownloadButtons({
  className,
  buttonClassName,
  size = 'lg',
  appearance = 'default',
  includePwa = false,
  deviceAware = false,
  webAppHref = '/app-beta',
  onStoreClick,
}: StoreDownloadButtonsProps) {
  const [platform, setPlatform] = useState<DevicePlatform>('desktop')

  useEffect(() => {
    setPlatform(resolvePlatform())
  }, [])

  const stores: Array<'apple' | 'google'> =
    deviceAware && platform === 'ios'
      ? ['apple']
      : deviceAware && platform === 'android'
        ? ['google']
        : ['apple', 'google']
  const shouldDeemphasizePwa = deviceAware && platform !== 'desktop'
  const columnsClass =
    stores.length + (includePwa ? 1 : 0) >= 3
      ? 'sm:grid-cols-3'
      : 'sm:grid-cols-2'

  return (
    <div
      className={cn(
        appearance === 'hero'
          ? 'flex w-full flex-wrap items-center gap-2'
          : cn('grid w-full grid-cols-1 gap-3', columnsClass),
        className
      )}
    >
      {stores.includes('apple') && (
        <StoreBadge
          label="Download on the App Store"
          imageAlt="Download on the App Store"
          imageSrc={APP_STORE_BADGE_URL}
          href={APP_STORE_URL}
          size={size}
          kind="apple"
          appearance={appearance}
          className={buttonClassName}
          onClick={() => onStoreClick?.('apple')}
        />
      )}
      {stores.includes('google') && (
        <StoreBadge
          label="Get it on Google Play"
          imageAlt="Get it on Google Play"
          imageSrc={GOOGLE_PLAY_BADGE_URL}
          href={GOOGLE_PLAY_URL}
          size={size}
          kind="google"
          appearance={appearance}
          className={buttonClassName}
          onClick={() => onStoreClick?.('google')}
        />
      )}
      {includePwa && (
        <PwaBadge
          size={size}
          href={webAppHref}
          appearance={appearance}
          visualPriority={shouldDeemphasizePwa ? 'secondary' : 'primary'}
          className={buttonClassName}
          onClick={() => onStoreClick?.('pwa')}
        />
      )}
    </div>
  )
}
